import React, { ReactElement, useRef, useState, useEffect } from 'react';
import { type SxProps, type Theme } from '@mui/material';
import { Tooltip } from '@/shared/components/Tooltip';

type EllipsisTooltipProps = {
  title?: string;
  // React 19 types `ref` as a regular prop; cloneElement needs that on the element props type.
  children: ReactElement<{ ref?: React.Ref<HTMLDivElement | null> }>;
  tooltipSx?: SxProps<Theme>;
};

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({ title, children, tooltipSx }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const target = (wrapper.firstElementChild as HTMLElement | null) ?? wrapper;

    const checkTruncation = () => {
      if (!target) return;
      const hasOverflow = target.scrollWidth > target.clientWidth;
      setIsTruncated(hasOverflow);
    };

    checkTruncation();

    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [title]);

  const childWithRef = React.cloneElement(children, {
    ref: (node: HTMLDivElement | null) => {
      wrapperRef.current = node;

      const originalRef = children.props.ref;
      if (typeof originalRef === 'function') {
        originalRef(node);
      } else if (originalRef && typeof originalRef === 'object') {
        originalRef.current = node;
      }
    },
  });

  if (!title) {
    return childWithRef;
  }

  return (
    <Tooltip
      title={isTruncated ? title : undefined}
      placement="top"
      slotProps={tooltipSx ? { tooltip: { sx: tooltipSx } } : undefined}
    >
      {childWithRef}
    </Tooltip>
  );
};
